import { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useUserData } from '@nhost/react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  PlusIcon,
  RefreshIcon,
  XIcon,
  SearchIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { useStore } from 'context/store-context';
import { GENERATE_INSTALL_LINK, UPDATE_STORE } from 'gql/mutations';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import { Modal } from 'components/Modal';
import { beginShopifyReauthorization } from 'utils/shopifyReauthorization';
import { KlaviyoIntegration } from './KlaviyoIntegration';

export const Integrations = () => {
  const {
    userStores,
    storeId,
    storeProperties,
    storeLoading,
    updateUserStore,
  } = useStore();

  const [showAddModal, setShowAddModal] = useState(false);
  const [newStoreUrl, setNewStoreUrl] = useState('');
  const [confirmedStoreUrl, setConfirmedStoreUrl] = useState(false);
  const [generateInstallLinkError, setGenerateInstallLinkError] = useState<
    string | null
  >(null);
  const [installLink, setInstallLink] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllStores, setShowAllStores] = useState(false);
  const [reconnectingStoreId, setReconnectingStoreId] = useState<string | null>(
    null,
  );
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());
  const [disconnectStoreId, setDisconnectStoreId] = useState<string | null>(
    null,
  );
  const [disconnectStoreName, setDisconnectStoreName] = useState<string | null>(
    null,
  );
  const INITIAL_STORES_TO_SHOW = 9;

  const userData = useUserData();
  const [activeAddStoreLoading, setActiveAddStoreLoading] = useState(false);

  interface AttemptToken {
    id: number;
    kind: 'add-store' | 'reconnect';
    targetId: string;
  }

  const isMountedRef = useRef(true);
  const attemptIdCounter = useRef(0);
  const currentAttemptRef = useRef<AttemptToken | null>(null);

  const prevStoreId = useRef(storeId);
  const prevUserId = useRef(userData?.id ?? null);

  useLayoutEffect(() => {
    isMountedRef.current = true;
    let invalidated = false;

    if (prevStoreId.current !== storeId) {
      prevStoreId.current = storeId;
      invalidated = true;
    }

    const currentUserId = userData?.id ?? null;
    if (prevUserId.current !== currentUserId) {
      prevUserId.current = currentUserId;
      invalidated = true;
    }

    if (invalidated) {
      currentAttemptRef.current = null;
      setReconnectingStoreId(null);
      setActiveAddStoreLoading(false);
      setShowAddModal(false);
      setNewStoreUrl('');
      setConfirmedStoreUrl(false);
      setInstallLink('');
      setGenerateInstallLinkError(null);
    }

    return () => {
      isMountedRef.current = false;
      currentAttemptRef.current = null;
    };
  }, [storeId, userData?.id]);

  const resetOutcomeAndInputs = () => {
    setInstallLink('');
    setConfirmedStoreUrl(false);
    setGenerateInstallLinkError(null);
    setNewStoreUrl('');
  };

  const handleCloseAddModal = () => {
    currentAttemptRef.current = null;
    setActiveAddStoreLoading(false);
    setReconnectingStoreId(null);
    resetOutcomeAndInputs();
    setShowAddModal(false);
  };

  const [generateInstallLink] = useMutation(GENERATE_INSTALL_LINK);

  const [updateStore, { loading: disconnectLoading }] = useMutation(
    UPDATE_STORE,
    {
      onCompleted: () => {
        // Close the modal and refresh the page to show updated store status
        setDisconnectStoreId(null);
        setDisconnectStoreName(null);
        window.location.reload();
      },
      onError: (error: any) => {
        // Check if the error is about uninstalledAt not being in stores_set_input
        const errorMessage = error?.message || '';
        if (
          errorMessage.includes('uninstalledAt') ||
          errorMessage.includes('stores_set_input')
        ) {
          setGenerateInstallLinkError(
            'Unable to disconnect store: The uninstalledAt field is not available in the current schema. Please uninstall the app directly from your Shopify admin: Settings → Apps and sales channels → ShipAid → Uninstall.',
          );
        } else {
          setGenerateInstallLinkError(
            errorMessage || 'Failed to disconnect store',
          );
        }
        setDisconnectStoreId(null);
        setDisconnectStoreName(null);
      },
    },
  );

  const handleConnectStore = async () => {
    if (activeAddStoreLoading) {
      return;
    }
    if (!newStoreUrl.includes('.myshopify.com')) {
      setGenerateInstallLinkError('Please enter a valid Shopify URL');
      return;
    }

    setReconnectingStoreId(null);
    setInstallLink('');
    setConfirmedStoreUrl(false);
    setGenerateInstallLinkError(null);
    setActiveAddStoreLoading(true);

    const attempt: AttemptToken = {
      id: ++attemptIdCounter.current,
      kind: 'add-store',
      targetId: 'add-store',
    };
    currentAttemptRef.current = attempt;

    const normalizedStore = newStoreUrl
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '');

    let mutatePromise: Promise<any>;
    try {
      mutatePromise = generateInstallLink({
        variables: {
          store: normalizedStore,
        },
      });
    } catch (syncErr) {
      if (isMountedRef.current && currentAttemptRef.current === attempt) {
        currentAttemptRef.current = null;
        setActiveAddStoreLoading(false);
        setInstallLink('');
        setConfirmedStoreUrl(false);
        setGenerateInstallLinkError(
          "We couldn't get a Shopify authorization link. Try again.",
        );
      }
      return;
    }

    try {
      const result = await mutatePromise;
      if (!isMountedRef.current || currentAttemptRef.current !== attempt) {
        return;
      }

      if (result?.errors && result.errors.length > 0) {
        currentAttemptRef.current = null;
        setActiveAddStoreLoading(false);
        setInstallLink('');
        setConfirmedStoreUrl(false);
        setGenerateInstallLinkError(
          "We couldn't get a Shopify authorization link. Try again.",
        );
        return;
      }

      const link = result?.data?.generateInstallLink;
      if (typeof link !== 'string' || link.trim() === '') {
        currentAttemptRef.current = null;
        setActiveAddStoreLoading(false);
        setInstallLink('');
        setConfirmedStoreUrl(false);
        setGenerateInstallLinkError(
          "Shopify didn't return an authorization link. Try again.",
        );
        return;
      }

      try {
        beginShopifyReauthorization(link);
        if (!isMountedRef.current || currentAttemptRef.current !== attempt) {
          return;
        }
        currentAttemptRef.current = null;
        setActiveAddStoreLoading(false);
        setInstallLink(link);
        setConfirmedStoreUrl(true);
        setGenerateInstallLinkError(null);
      } catch (error) {
        if (!isMountedRef.current || currentAttemptRef.current !== attempt) {
          return;
        }
        currentAttemptRef.current = null;
        setInstallLink('');
        setConfirmedStoreUrl(false);
        setActiveAddStoreLoading(false);
        setGenerateInstallLinkError("We couldn't open Shopify. Try again.");
      }
    } catch (asyncErr) {
      if (!isMountedRef.current || currentAttemptRef.current !== attempt) {
        return;
      }
      currentAttemptRef.current = null;
      setInstallLink('');
      setConfirmedStoreUrl(false);
      setActiveAddStoreLoading(false);
      setGenerateInstallLinkError(
        "We couldn't get a Shopify authorization link. Try again.",
      );
    }
  };

  const handleManage = (store: any) => {
    if (!store || !store.id) {
      console.warn('handleManage: Invalid store object', store);
      return;
    }

    // Find the full store object from userStores
    const fullStore = userStores?.find((s) => s.storeId === store.id);
    if (!fullStore) {
      console.warn('handleManage: Store not found in userStores', store.id);
      return;
    }

    // Always switch to this store (even if already active, to refresh context)
    if (updateUserStore) {
      updateUserStore(fullStore);

      // Navigate to home page to see the store's dashboard
      // Use window.location for a full page reload to ensure store context is refreshed
      window.location.href = '/';
    } else {
      console.warn('handleManage: updateUserStore is not available');
    }
  };

  const handleReconnect = async (store: any) => {
    if (!store || (!store.domain && !store.url)) {
      setGenerateInstallLinkError('Store information is missing');
      return;
    }

    if (
      currentAttemptRef.current?.kind === 'reconnect' &&
      currentAttemptRef.current?.targetId === store.id
    ) {
      return;
    }

    // Clear other busy and previous outcomes
    currentAttemptRef.current = null;
    setActiveAddStoreLoading(false);
    setInstallLink('');
    setConfirmedStoreUrl(false);
    setGenerateInstallLinkError(null);
    setReconnectingStoreId(store.id);

    const attempt: AttemptToken = {
      id: ++attemptIdCounter.current,
      kind: 'reconnect',
      targetId: store.id,
    };
    currentAttemptRef.current = attempt;

    // Public and custom app identities are selected by the authenticated server flow.
    const storeUrl = store.domain || store.url;
    if (!storeUrl) {
      currentAttemptRef.current = null;
      setInstallLink('');
      setConfirmedStoreUrl(false);
      setGenerateInstallLinkError('Store URL is missing');
      setReconnectingStoreId(null);
      return;
    }

    let mutatePromise: Promise<any>;
    try {
      mutatePromise = generateInstallLink({
        variables: {
          store: storeUrl,
        },
      });
    } catch (syncErr) {
      if (isMountedRef.current && currentAttemptRef.current === attempt) {
        currentAttemptRef.current = null;
        setInstallLink('');
        setConfirmedStoreUrl(false);
        setReconnectingStoreId(null);
        setGenerateInstallLinkError(
          "We couldn't get a Shopify authorization link. Try again.",
        );
      }
      return;
    }

    try {
      const result = await mutatePromise;
      if (!isMountedRef.current || currentAttemptRef.current !== attempt) {
        return;
      }

      if (result?.errors && result.errors.length > 0) {
        currentAttemptRef.current = null;
        setReconnectingStoreId(null);
        setInstallLink('');
        setConfirmedStoreUrl(false);
        setGenerateInstallLinkError(
          "We couldn't get a Shopify authorization link. Try again.",
        );
        return;
      }

      const link = result?.data?.generateInstallLink;
      if (typeof link !== 'string' || link.trim() === '') {
        currentAttemptRef.current = null;
        setReconnectingStoreId(null);
        setInstallLink('');
        setConfirmedStoreUrl(false);
        setGenerateInstallLinkError(
          "Shopify didn't return an authorization link. Try again.",
        );
        return;
      }

      try {
        beginShopifyReauthorization(link);
        if (!isMountedRef.current || currentAttemptRef.current !== attempt) {
          return;
        }
        currentAttemptRef.current = null;
        setInstallLink(link);
        setConfirmedStoreUrl(true);
        setReconnectingStoreId(null);
        setGenerateInstallLinkError(null);
      } catch (error) {
        if (!isMountedRef.current || currentAttemptRef.current !== attempt) {
          return;
        }
        currentAttemptRef.current = null;
        setInstallLink('');
        setConfirmedStoreUrl(false);
        setReconnectingStoreId(null);
        setGenerateInstallLinkError("We couldn't open Shopify. Try again.");
      }
    } catch (asyncErr) {
      if (!isMountedRef.current || currentAttemptRef.current !== attempt) {
        return;
      }
      currentAttemptRef.current = null;
      setInstallLink('');
      setConfirmedStoreUrl(false);
      setReconnectingStoreId(null);
      setGenerateInstallLinkError(
        "We couldn't get a Shopify authorization link. Try again.",
      );
    }
  };

  const handleDisconnect = (store: any) => {
    if (!store || !store.id) {
      return;
    }
    setDisconnectStoreId(store.id);
    setDisconnectStoreName(store.name);
  };

  const confirmDisconnect = () => {
    if (!disconnectStoreId) {
      return;
    }

    // Attempt to set uninstalledAt via UPDATE_STORE mutation
    // Note: This may fail if the GraphQL schema doesn't allow setting this field
    updateStore({
      variables: {
        storeId: disconnectStoreId,
        set: {
          uninstalledAt: new Date().toISOString(),
        },
      },
    });
  };

  const stores = useMemo(() => {
    if (!userStores || userStores.length === 0) {
      return [];
    }
    return userStores
      .filter((store) => store && store.storeId)
      .map((store) => {
        const isCurrentStore = store.storeId === storeId;
        // Use logoUrl from the store object (from GET_STORES) or storeProperties for current store
        const logoUrl =
          store.logoUrl || (isCurrentStore ? storeProperties?.logoUrl : null);

        // Determine connection status:
        // - If it's the current store, check storeProperties AND store.uninstalledAt
        // - If it's not the current store, check store.uninstalledAt from GET_STORES
        const isUninstalled = isCurrentStore
          ? storeProperties?.uninstalledAt
          : (store as any).uninstalledAt;
        const hasStoreUrl = isCurrentStore
          ? storeProperties?.store
          : store.domain; // If not current store, check if it has a domain from GET_STORES

        return {
          id: store.storeId,
          name: store.name || store.domain || 'Unnamed Store',
          url: store.domain || '',
          connected: Boolean(store.storeId && !isUninstalled && hasStoreUrl),
          logo: logoUrl || '/shopify-icon.png',
          isCurrentStore,
          initials:
            store.initials || store.name?.substring(0, 2).toUpperCase() || 'ST',
        };
      });
  }, [userStores, storeId, storeProperties]);

  const filteredStores = useMemo(() => {
    if (!searchQuery.trim()) {
      return stores;
    }
    const query = searchQuery.toLowerCase();
    return stores.filter(
      (store) =>
        store.name.toLowerCase().includes(query) ||
        store.url.toLowerCase().includes(query),
    );
  }, [stores, searchQuery]);

  const displayedStores = useMemo(() => {
    if (showAllStores || filteredStores.length <= INITIAL_STORES_TO_SHOW) {
      return filteredStores;
    }
    return filteredStores.slice(0, INITIAL_STORES_TO_SHOW);
  }, [filteredStores, showAllStores]);

  const integrations = {
    csTools: [
      {
        name: 'Gorgias',
        desc: 'Customer service & support helpdesk',
        logo: '/gorgias.jpg',
        link: 'https://www.gorgias.com/apps/shipaid',
      },
      {
        name: 'Zendesk',
        desc: 'Unified support & ticket management',
        logo: 'https://seeklogo.com/images/Z/zendesk-logo-703FE8BC79-seeklogo.com.png',
        link: undefined,
      },
    ],
    subscriptionApps: [
      {
        name: 'Awtomic',
        desc: 'Advanced subscription platform',
        logo: 'https://global-uploads.webflow.com/63e54b5c8e98c140b66b30cc/63e7e0dcb97c55e06405d495_awtomic-logo%402x.png',
        link: undefined,
      },
      {
        name: 'Recharge',
        desc: 'Recurring billing for ecommerce',
        logo: 'https://cdn.worldvectorlogo.com/logos/recharge.svg',
        link: undefined,
      },
    ],
    other: [
      {
        name: 'ShipStation',
        desc: 'Shipping & fulfillment',
        logo: 'https://cdn.worldvectorlogo.com/logos/shipstation.svg',
        link: undefined,
      },
    ],
  };

  return (
    <SettingsViewWrapper activeNav="integration">
      <div className="pb-8 max-w-6xl mx-auto px-4 sm:px-6 md:px-8 pt-6">
        {/* Store Connections Section */}
        <section className="mb-14">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-[28px] font-semibold text-[#202223] leading-tight mb-2">
                Store Connections
              </h1>
              <p className="text-[#6d7175] text-sm max-w-2xl leading-relaxed">
                Connect your ecommerce stores to sync orders and manage claims.
              </p>
            </div>
            {stores.length > 0 && (
              <div className="relative w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search stores..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            )}
          </div>

          {searchQuery && filteredStores.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No stores found matching &quot;{searchQuery}&quot;
            </div>
          )}

          {generateInstallLinkError && (
            <div className="mb-4 flex items-center text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
              <ExclamationCircleIcon className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>{generateInstallLinkError}</span>
              <button
                type="button"
                onClick={() => setGenerateInstallLinkError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {storeLoading && (
              <div className="col-span-full text-center py-8 text-gray-500">
                Loading stores...
              </div>
            )}
            {!storeLoading && stores.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                No stores connected yet. Click &quot;Add New Store&quot; to get
                started.
              </div>
            )}
            {displayedStores.map((store) => (
              <div
                key={store.id}
                className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                    {store.logo &&
                    store.logo !== '/shopify-icon.png' &&
                    !failedLogos.has(store.id) ? (
                      <img
                        src={store.logo}
                        alt={`${store.name} logo`}
                        className="w-full h-full object-cover"
                        onError={() => {
                          setFailedLogos((prev) => new Set(prev).add(store.id));
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {failedLogos.has(store.id) ? (
                          <span className="text-xs font-semibold text-gray-600">
                            {store.initials}
                          </span>
                        ) : (
                          <img
                            src="/shopify-icon.png"
                            alt="Shopify store"
                            className="w-6 h-6 object-contain"
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{store.name}</p>
                    {store.connected ? (
                      <span className="inline-flex items-center text-green-600 bg-green-100 px-2 py-0.5 rounded-full text-xs mt-1">
                        <CheckCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" />{' '}
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-red-600 bg-red-100 px-2 py-0.5 rounded-full text-xs mt-1">
                        <ExclamationCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" />{' '}
                        Disconnected
                      </span>
                    )}
                  </div>
                </div>

                <p
                  className="text-sm text-gray-500 mb-4 truncate"
                  title={store.url}
                >
                  {store.url}
                </p>

                <div className="flex flex-wrap gap-2">
                  {!store.connected && (
                    <button
                      type="button"
                      onClick={() => handleReconnect(store)}
                      disabled={reconnectingStoreId === store.id}
                      className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {reconnectingStoreId === store.id ? (
                        <>
                          <RefreshIcon className="w-4 h-4 animate-spin" />{' '}
                          Connecting...
                        </>
                      ) : (
                        <>
                          <RefreshIcon className="w-4 h-4" /> Reconnect
                        </>
                      )}
                    </button>
                  )}

                  {store.connected && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleManage(store)}
                        className="border border-gray-200 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
                      >
                        Manage
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReconnect(store)}
                        disabled={reconnectingStoreId === store.id}
                        className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {reconnectingStoreId === store.id ? (
                          <>
                            <RefreshIcon className="w-4 h-4 animate-spin" />{' '}
                            Connecting...
                          </>
                        ) : (
                          <>
                            <RefreshIcon className="w-4 h-4" /> Reconnect
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDisconnect(store)}
                        className="flex items-center gap-1 border border-red-300 text-red-600 px-3 py-1.5 rounded-lg text-sm hover:bg-red-50 transition-colors whitespace-nowrap"
                      >
                        <TrashIcon className="w-4 h-4 flex-shrink-0" />{' '}
                        <span className="hidden sm:inline">Disconnect</span>
                        <span className="sm:hidden">Disc</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}

            {(!searchQuery || filteredStores.length > 0) && (
              <button
                type="button"
                onClick={() => {
                  currentAttemptRef.current = null;
                  setActiveAddStoreLoading(false);
                  setReconnectingStoreId(null);
                  resetOutcomeAndInputs();
                  setShowAddModal(true);
                }}
                className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-blue-50 hover:border-blue-400 transition-all rounded-xl p-6 text-gray-500"
              >
                <PlusIcon className="w-7 h-7 text-blue-500" />
                <span className="mt-2 text-sm">Add New Store</span>
              </button>
            )}
          </div>

          {/* Load More / Show Less */}
          {filteredStores.length > INITIAL_STORES_TO_SHOW && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setShowAllStores(!showAllStores)}
                className="px-6 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                {showAllStores
                  ? `Show Less (${INITIAL_STORES_TO_SHOW} stores)`
                  : `Show All (${filteredStores.length} stores)`}
              </button>
              {!showAllStores && (
                <p className="mt-2 text-sm text-gray-500">
                  Showing {INITIAL_STORES_TO_SHOW} of {filteredStores.length}{' '}
                  stores
                </p>
              )}
            </div>
          )}
        </section>

        {/* Partner Integrations Section */}
        <section>
          <h1 className="text-[28px] font-semibold text-[#202223] leading-tight mb-2">
            Partner Integrations
          </h1>
          <p className="text-gray-500 mb-8">
            Enhance workflows with your favorite ecommerce tools.
          </p>

          {[
            {
              label: 'Customer Service Tools',
              items: integrations.csTools,
            },
            {
              label: 'Subscription Apps',
              items: integrations.subscriptionApps,
            },
            {
              label: 'Other Integrations',
              items: integrations.other,
            },
          ].map((cat) => (
            <div key={cat.label} className="mb-12">
              <h2 className="text-xl font-semibold mb-4">{cat.label}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cat.label === 'Other Integrations' && (
                  <div className="col-span-full">
                    <KlaviyoIntegration />
                  </div>
                )}
                {cat.items.map((tool) => {
                  const isComingSoon = !tool.link;
                  return (
                    <div
                      key={tool.name}
                      onClick={() =>
                        tool.link && window.open(tool.link, '_blank')
                      }
                      className={`flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm transition-all relative ${
                        tool.link
                          ? 'cursor-pointer hover:shadow-md'
                          : 'opacity-75'
                      }`}
                    >
                      <img
                        src={tool.logo}
                        alt={tool.name}
                        className="w-10 h-10 rounded-md object-contain bg-gray-50 p-1"
                        onError={(e) => {
                          // Fallback to a placeholder if logo fails to load
                          e.currentTarget.src = '/shopify-icon.png';
                        }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{tool.name}</p>
                          {isComingSoon && (
                            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              Coming Soon
                            </span>
                          )}
                        </div>
                        <p className="text-gray-500 text-sm">{tool.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        {/* Add Store Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold">Connect a New Store</h3>
                <button
                  type="button"
                  onClick={handleCloseAddModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your Shopify store URL
                  </label>
                  <input
                    type="text"
                    value={newStoreUrl}
                    onChange={(e) => {
                      setNewStoreUrl(e.target.value);
                      setGenerateInstallLinkError(null);
                      if (currentAttemptRef.current?.kind === 'add-store') {
                        currentAttemptRef.current = null;
                        setActiveAddStoreLoading(false);
                      }
                    }}
                    placeholder="your-store.myshopify.com"
                    disabled={confirmedStoreUrl}
                    className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>

                {generateInstallLinkError && (
                  <div className="flex items-center text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
                    <ExclamationCircleIcon className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span>{generateInstallLinkError}</span>
                  </div>
                )}

                {confirmedStoreUrl && installLink && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center text-green-700">
                      <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                      <span className="text-sm font-medium">
                        Store confirmed! Click the button below to install the
                        app on Shopify.
                      </span>
                    </div>
                    <a
                      href={installLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                    >
                      Install App on Shopify
                    </a>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCloseAddModal}
                    className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  {!confirmedStoreUrl && (
                    <button
                      type="button"
                      onClick={handleConnectStore}
                      disabled={
                        !newStoreUrl.includes('.myshopify.com') ||
                        activeAddStoreLoading
                      }
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {activeAddStoreLoading
                        ? 'Connecting...'
                        : 'Connect Store'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disconnect Confirmation Modal */}
        <Modal
          open={disconnectStoreId !== null}
          openModal={(value) => {
            if (!value) {
              setDisconnectStoreId(null);
              setDisconnectStoreName(null);
            }
          }}
          containerClass="sm:max-w-md sm:w-full shadow-lg"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Disconnect Store
              </h3>
              <button
                type="button"
                onClick={() => {
                  setDisconnectStoreId(null);
                  setDisconnectStoreName(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to disconnect{' '}
                <strong>{disconnectStoreName}</strong>?
              </p>
              <p className="text-sm text-gray-500">
                This will disconnect the store from ShipAid. You can reconnect
                it later if needed.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setDisconnectStoreId(null);
                  setDisconnectStoreName(null);
                }}
                disabled={disconnectLoading}
                className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDisconnect}
                disabled={disconnectLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {disconnectLoading ? 'Disconnecting...' : 'Disconnect Store'}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </SettingsViewWrapper>
  );
};

export default Integrations;
